import { useState } from "react";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Save, X, Camera, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ImageUploader from "./ImageUploader";


export default function BandPhotosTab({ photos, onRefresh, endpoint = "/music/photos" }) {
  const [form, setForm] = useState({
    image_url: "",
    caption: "",
    order: 0,
    active: true
  });
  const [editing, setEditing] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const sortedPhotos = [...photos].sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.image_url) {
      toast.error("Selecciona o sube una imagen");
      return;
    }
    try {
      if (editing) {
        await adminApi.put(`${endpoint}/${editing.id}`, form);
        toast.success("Foto actualizada");
      } else {
        await adminApi.post(`${endpoint}`, form);
        toast.success("Foto agregada");
      }
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error("Error al guardar foto");
    }
  };

  const handleEdit = (photo) => {
    setEditing(photo);
    setForm({
      image_url: photo.image_url,
      caption: photo.caption || "",
      order: photo.order || 0,
      active: photo.active
    });
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Eliminar esta foto?")) return;
    try {
      await adminApi.delete(`${endpoint}/${id}`);
      toast.success("Foto eliminada");
      onRefresh();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const resetForm = () => {
    setEditing(null);
    setForm({ image_url: "", caption: "", order: 0, active: true });
  };

  // Drag handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newPhotos = [...sortedPhotos];
    const [draggedItem] = newPhotos.splice(draggedIndex, 1);
    newPhotos.splice(dropIndex, 0, draggedItem);

    try {
      await Promise.all(
        newPhotos.map((photo, index) => 
          adminApi.put(`${endpoint}/${photo.id}`, { order: index })
        )
      );
      toast.success("Orden actualizado");
      onRefresh();
    } catch (error) {
      toast.error("Error al reordenar");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Form */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
        <h2 className="text-xl font-bold text-black mb-4 flex items-center gap-2">
          <Camera className="text-[#E91E8C]" />
          {editing ? "Editar Foto" : "Nueva Foto"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1 font-medium">Imagen *</label>
            <ImageUploader
              value={form.image_url}
              onChange={(url) => setForm({...form, image_url: url})}
              category="thumbnails"
              recommendedSize="Tamaño recomendado: 800x800px o similar"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1 font-medium">Descripción (opcional)</label>
            <Input
              value={form.caption}
              onChange={(e) => setForm({...form, caption: e.target.value})}
              placeholder="Descripción de la foto"
              className="bg-gray-50 border-gray-200 text-black"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({...form, active: e.target.checked})}
              className="rounded"
            />
            <label className="text-sm text-gray-600">Activo (visible en la página)</label>
          </div>

          <div className="flex gap-2">
            <Button type="submit" className="bg-[#E91E8C] hover:bg-[#E91E8C]/90 text-white flex-1">
              <Save size={16} className="mr-2" />
              {editing ? "Actualizar" : "Agregar Foto"}
            </Button>
            {editing && (
              <Button type="button" variant="outline" onClick={resetForm}>
                <X size={16} className="mr-1" /> Cancelar
              </Button>
            )}
          </div>
        </form>
      </div>

      {/* Gallery Grid */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
        <h2 className="text-xl font-bold text-black mb-2">Galería ({photos.length} fotos)</h2>
        <p className="text-sm text-gray-500 mb-4">Arrastra para reordenar</p>
        
        {sortedPhotos.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No hay fotos en la galería</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[500px] overflow-y-auto">
            {sortedPhotos.map((photo, index) => (
              <div
                key={photo.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                className={`relative aspect-square rounded-xl overflow-hidden cursor-move group border-2 transition-all ${
                  dragOverIndex === index ? 'border-[#E91E8C] ring-2 ring-[#E91E8C]/30' : 'border-transparent'
                } ${draggedIndex === index ? 'opacity-50' : ''} ${!photo.active ? 'opacity-60' : ''}`}
              >
                <img 
                  src={photo.image_url}
                  alt={photo.caption || "Foto de banda"}
                  className="w-full h-full object-cover"
                />
                
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="secondary" 
                      onClick={() => handleEdit(photo)} 
                      className="h-8 w-8 p-0"
                    >
                      <Edit2 size={14} />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="destructive" 
                      onClick={() => handleDelete(photo.id)} 
                      className="h-8 w-8 p-0"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  <GripVertical size={20} className="text-white/70 mt-2" />
                </div>

                {/* Status indicator */}
                {!photo.active && (
                  <div className="absolute top-1 left-1 bg-red-500 text-white text-xs px-2 py-0.5 rounded">
                    Oculta
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
